import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { MockBadge, Spinner } from "../components/ui";
import { ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const METHOD_COLOR = {
  get: "text-signal-green border-signal-green/40",
  post: "text-signal-yellow border-signal-yellow/40",
  put: "text-blue-400 border-blue-400/40",
  patch: "text-blue-400 border-blue-400/40",
  delete: "text-signal-red border-signal-red/40",
};

export default function ApiDocs() {
  const { t } = useI18n();
  const [spec, setSpec] = useState(null);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/openapi.json").then(({ data }) => setSpec(data));
  }, []);

  if (!spec) return <div className="p-10"><Spinner /></div>;
  if (!spec.paths) return <div className="p-10 font-mono text-xs text-text-muted">{t("apiDocs.specUnavailable")}</div>;

  // group paths by tag
  const byTag = {};
  Object.entries(spec.paths || {}).forEach(([path, methods]) => {
    Object.entries(methods || {}).forEach(([method, op]) => {
      if (!["get", "post", "put", "patch", "delete"].includes(method)) return;
      const tag = (op.tags && op.tags[0]) || "misc";
      byTag[tag] = byTag[tag] || [];
      byTag[tag].push({ path, method, op });
    });
  });

  const f = filter.toLowerCase().trim();
  const filtered = {};
  Object.entries(byTag).forEach(([tag, ops]) => {
    const o = ops.filter((x) => !f || x.path.toLowerCase().includes(f) || (x.op.summary || "").toLowerCase().includes(f));
    if (o.length) filtered[tag] = o;
  });

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("apiDocs.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("apiDocs.title")}</h1>
          <p className="mt-2 text-xs text-text-muted max-w-xl">
            {t("apiDocs.subtitle", { title: spec.info.title, version: spec.info.version, count: Object.keys(spec.paths).length })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/docs`}
            target="_blank"
            rel="noreferrer"
            data-testid="apidocs-swagger-link"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest2 border border-zinc-700 text-text-secondary hover:border-white hover:text-white"
          >
            <ExternalLink size={14} /> {t("apiDocs.swaggerUi")}
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/redoc`}
            target="_blank"
            rel="noreferrer"
            data-testid="apidocs-redoc-link"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest2 border border-zinc-700 text-text-secondary hover:border-white hover:text-white"
          >
            <ExternalLink size={14} /> {t("apiDocs.redoc")}
          </a>
          <MockBadge />
        </div>
      </div>

      <div className="grid grid-cols-12">
        <aside className="col-span-12 lg:col-span-3 border-r border-border p-5">
          <div className="overline">{t("apiDocs.filter")}</div>
          <input
            data-testid="apidocs-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="/api/listings"
            className="mt-2 w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none"
          />
          <div className="mt-5 overline">{t("apiDocs.tags")}</div>
          <div className="mt-2 space-y-1">
            {Object.keys(filtered).sort().map((tag) => (
              <a
                key={tag}
                href={`#tag-${tag}`}
                className="block text-[11px] font-mono uppercase tracking-widest2 text-text-secondary hover:text-signal-green"
              >
                {tag} <span className="text-text-muted">· {filtered[tag].length}</span>
              </a>
            ))}
          </div>
        </aside>

        <div className="col-span-12 lg:col-span-9 divide-y divide-border">
          {Object.entries(filtered).sort().map(([tag, ops]) => {
            const tagMeta = spec.tags?.find((x) => x.name === tag);
            return (
              <section key={tag} id={`tag-${tag}`} className="p-6">
                <div className="overline">{tag}</div>
                <h3 className="mt-1 font-heading text-2xl font-bold capitalize">{tag}</h3>
                {tagMeta?.description && (
                  <p className="mt-1 text-xs text-text-muted">{tagMeta.description}</p>
                )}
                <div className="mt-4 divide-y divide-border border border-border">
                  {ops.map(({ path, method, op }, i) => {
                    const key = `${method}-${path}`;
                    const isOpen = open[key];
                    return (
                      <div key={key}>
                        <button
                          onClick={() => setOpen({ ...open, [key]: !isOpen })}
                          data-testid={`op-${method}-${path.replace(/[^a-z0-9]/gi, "-")}`}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surfaceHover transition-colors"
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest2 border ${METHOD_COLOR[method] || "text-white border-border"}`}>
                            {method}
                          </span>
                          <code className="flex-1 font-mono text-xs text-white truncate">{path}</code>
                          <span className="text-[10px] font-mono text-text-muted truncate max-w-xs">{op.summary || ""}</span>
                        </button>
                        {isOpen && (
                          <div className="px-4 py-4 bg-surface text-xs font-mono space-y-3">
                            {op.description && <div className="text-text-secondary whitespace-pre-wrap">{op.description}</div>}
                            {op.parameters?.length > 0 && (
                              <div>
                                <div className="overline mb-2">{t("apiDocs.parameters")}</div>
                                <ul className="space-y-1">
                                  {op.parameters.map((p) => (
                                    <li key={p.name} className="flex gap-2">
                                      <span className="text-signal-green">{p.name}</span>
                                      <span className="text-text-muted">{t("apiDocs.in")} {p.in}</span>
                                      <span className="text-text-muted">{p.required ? t("apiDocs.required") : t("apiDocs.optional")}</span>
                                      <span className="text-text-muted">· {p.schema?.type || "—"}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div>
                              <div className="overline mb-2">{t("apiDocs.responses")}</div>
                              <ul className="space-y-1">
                                {Object.entries(op.responses || {}).map(([code, r]) => (
                                  <li key={code} className="flex gap-2">
                                    <span className={code.startsWith("2") ? "text-signal-green" : code.startsWith("4") ? "text-signal-yellow" : "text-signal-red"}>{code}</span>
                                    <span className="text-text-secondary">{r.description}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="text-[10px] text-text-muted">
                              {t("apiDocs.tryIt")} <code className="text-signal-green">curl -X {method.toUpperCase()} {process.env.REACT_APP_BACKEND_URL}{path}</code>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
